import { useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { acceptsFile, acceptsMimeType } from "@/utils/files";

export const FILE_TYPE_ERROR = "This file type isn't supported. Choose a supported file instead.";
export const ONE_FILE_ERROR = "Drop one file at a time.";

export type FileDropPhase = "idle" | "over" | "blocked";

function carriesFiles(e: DragEvent): boolean {
  return Array.from(e.dataTransfer.types).includes("Files");
}

function dragPhase(dataTransfer: DataTransfer, accept: string, multiple: boolean): FileDropPhase {
  const files = Array.from(dataTransfer.items).filter((item) => item.kind === "file");
  if (multiple) {
    return files.length > 0 && files.every((f) => acceptsMimeType(f.type, accept) === false) ? "blocked" : "over";
  }
  if (files.length !== 1) return "over";
  return acceptsMimeType(files[0].type, accept) === false ? "blocked" : "over";
}

function releasedInside(e: DragEvent<HTMLElement>): boolean {
  const r = e.currentTarget.getBoundingClientRect();
  return e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
}

export function useFileDrop({
  accept, busy, onFile, onFiles,
}: {
  accept: string;
  busy?: boolean;
  onFile?: (f: File) => void;
  onFiles?: (files: File[]) => void;
}) {
  const multiple = !!onFiles;
  const inputRef = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState<FileDropPhase>("idle");
  const [rejection, setRejection] = useState<string | null>(null);

  function currentAccept(): string {
    return inputRef.current?.accept || accept;
  }

  function take(files: File[]) {
    if (files.length === 0) return;
    if (onFiles) {
      const ok = files.filter((f) => acceptsFile(f, currentAccept()));
      const skipped = files.length - ok.length;
      setRejection(skipped === 0 ? null
        : ok.length === 0 ? FILE_TYPE_ERROR
        : `${skipped} file${skipped === 1 ? " isn't" : "s aren't"} a supported type and ${skipped === 1 ? "was" : "were"} skipped.`);
      if (ok.length > 0) onFiles(ok);
      return;
    }
    if (files.length > 1) {
      setRejection(ONE_FILE_ERROR);
      return;
    }
    if (!acceptsFile(files[0], currentAccept())) {
      setRejection(FILE_TYPE_ERROR);
      return;
    }
    setRejection(null);
    onFile?.(files[0]);
  }

  function onDragOver(e: DragEvent<HTMLElement>) {
    if (!carriesFiles(e)) return;
    e.preventDefault();
    const next = busy ? "idle" : dragPhase(e.dataTransfer, currentAccept(), multiple);
    e.dataTransfer.dropEffect = next === "over" ? "copy" : "none";
    setPhase(next);
  }

  function onDragLeave(e: DragEvent<HTMLElement>) {
    if (e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget)) return;
    const rejectedHere = phase === "blocked" && releasedInside(e);
    setPhase("idle");
    if (rejectedHere) setRejection(FILE_TYPE_ERROR);
  }

  function onDrop(e: DragEvent<HTMLElement>) {
    if (!carriesFiles(e)) return;
    e.preventDefault();
    setPhase("idle");
    if (busy) return;
    take(Array.from(e.dataTransfer.files));
  }

  return {
    phase,
    rejection,
    clearRejection: () => setRejection(null),
    openPicker: () => {
      if (!busy) inputRef.current?.click();
    },
    zoneProps: { onDragEnter: onDragOver, onDragOver, onDragLeave, onDrop },
    inputProps: {
      ref: inputRef,
      type: "file" as const,
      accept,
      multiple,
      disabled: busy,
      className: "hidden",
      onChange: (e: ChangeEvent<HTMLInputElement>) => {
        take(Array.from(e.target.files ?? []));
        e.target.value = "";
      },
    },
  };
}

export function fileDropTone({
  phase, busy, invalid, filled, attention,
}: {
  phase: FileDropPhase;
  busy?: boolean;
  invalid?: boolean;
  filled?: boolean;
  attention?: boolean;
}): string {
  if (busy) return "border-solid border-primary bg-primary/10 text-foreground cursor-wait";
  if (phase === "over") return "border-dashed border-primary bg-primary/10 text-foreground";
  if (phase === "blocked") return "border-dashed border-slate-300 bg-slate-100 text-slate-400 opacity-60 cursor-not-allowed";
  if (invalid) return "border-solid border-destructive bg-destructive/10 text-destructive hover:bg-destructive/15";
  if (filled) return "border-solid border-emerald-400 bg-emerald-50 text-emerald-800 hover:bg-emerald-100";
  if (attention) return "border-solid border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100";
  return "border-dashed border-input bg-muted/40 text-muted-foreground hover:border-primary hover:bg-primary/5";
}
